<template>
  <div class="container">
    <o-bread-crumb :url="{ name: 'MemberTier' }">
      <template v-slot:pageName>
        Member Tier
      </template>
      <template v-slot:pageDetail>
        Show Members
      </template>
    </o-bread-crumb>
    <div v-if="validation" class="custom-alert modern-alert mt-3 mb-3">
      <div class="alert-icon">
        <i class="fas fa-info-circle"></i>
      </div>
      <div class="alert-content">
        <h6 class="alert-title">Information</h6>
        <p class="alert-message">
          {{ validation }}
          <router-link v-if="showSyncLink" :to="{ name: 'MemberTier' }" class="alert-link">here</router-link>
        </p>
      </div>
    </div>
    <div class="card text-center bg-white shadow-none">
      <div class="card-header">
        <ul class="nav nav-pills card-header-pills justify-content-between ms-2 me-4 mt-2 mb-2">
          <li class="nav-item">
            <div class="d-flex d-inline-block">
              <h4 class="header-title">
                  <i class="fas fa-users-cog me-2 primary-icon"></i>Show Members
              </h4>
            </div>
            <p class="header-subtitle me-4 mt-1">Manage members in this tier</p>
          </li>
          <li class="nav-item d-flex align-items-center gap-2">
            <button class="btn btn-refresh" @click.prevent="load_state()" title="Refresh">
              <i class="fas fa-sync-alt"></i>
            </button>
          </li>
        </ul>
      </div>
      <div class="card-body">
        <div class="spinner d-flex justify-content-center align-items-center" v-if="page.isLoading">
          <div class="spinner-border text-primary" role="status"></div>
        </div>
        <div v-if="page.output">
          <o-table style="text-align:left;" class="table-hover member-list-table mt-4">
            <thead>
              <tr v-if="fields">
                <th class="fw-bolder text-muted" scope="col" v-for="(field, index) in fields" :key="index">
                  {{ field }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in ShowMemberTier" :key="index" class="member-row-clickable">
                <td>{{ (meta.page - 1) * meta.limit + index + 1 }}</td>
                <td>{{ item.memberName }}</td>
                <td>{{ item.memberId }}</td>
                <td>{{ item.tierName }}</td>
                <td>{{ item.tierId }}</td>
                <td>{{ item.totalTransactions.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,') }}</td>
                <td class="ml-3">
                  <div class="d-flex gap-2" @click.stop>
                      <button v-if="write" class="btn btn-action btn-delete" @click.prevent="destroy(item.memberTieringId, index)">
                        <i class="fas fa-trash-alt me-1"></i> Delete
                      </button>
                  </div>
                </td>
              </tr>
              <tr v-if="ShowMemberTier.length === 0">
                  <td colspan="7" class="text-center">No member registered</td>
              </tr>
            </tbody>
          </o-table>
        </div>
      </div>
      <div class="container mt-5">
        <o-pagination :page="meta.page" :total="meta.total" @callback="navPage" />
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <o-warning-box
        v-if="isDeleteConfirmationVisible"
        title="Remove Member from Tier"
        message="Are you sure you want to remove this member from this tier? This will reset their tiering status."
        action-text="Remove"
        cancel-text="Cancel"
        @action="confirmDelete"
        @cancel="cancelDelete"
        :hide-action="false"
    />
  </div>
</template>

<script>
import {
  OBreadCrumb,
  OTable,
  OPagination,
  OWarningBox
} from '@/components/OmnihControl';
import { mapState } from 'vuex';
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import store from '@/store/';
import { useToast } from '@/composables/useToast';

export default {
  setup() {
    const error = ref(null);
    const page = reactive({
      isLoading: false,
      output: false,
      component: 'member-tier'
    });

    const { showToast } = useToast();

    const selfLoaded = ref(store.state.Auth.selfLoaded);

    const write = ref(false);
    const validation = ref('');
    // For delete confirmation
    const isDeleteConfirmationVisible = ref(false);
    const memberTieringIdToDelete = ref(null);

    const isExpanded = reactive({
      before: [],
      after: []
    });
    let fields = ref([
      'No',
      'Member Name',
      'Member ID',
      'Tier Name',
      'Tier ID',
      'Total Transaction',
      'Actions'
    ]);

    // const searchBy = reactive({
    //     selected:"",
    //     criteria:"",
    // })

    // const searchOptions = reactive([
    //     { value: 'memberName', text: 'Member Name' },
    //     { value: 'memberId', text: 'Member ID' },
    //     { value: 'tierName', text: 'Tier Name' },
    //     { value: 'tierId', text: 'Tier ID' }
    // ])
    const showSyncLink = computed(() => validation.value.includes("Please click sync button to change"));
    let meta = computed(() => store.state.ShowMemberTier.meta);

    watch(
      () => store.state.Auth.selfLoaded,
      (newVal) => {
        if (newVal) {
          selfLoaded.value = newVal;
          load_state();
        }
      }
    );

    onMounted(() => {
      if (selfLoaded.value) {
        store.commit('Member/reset_meta');
        load_state();
      }
    });

    function destroy(memberTieringId) {
      memberTieringIdToDelete.value = memberTieringId;
      isDeleteConfirmationVisible.value = true;
    }

    function confirmDelete() {
      page.isLoading = true;
      
      store.dispatch('MemberTier/delete_member', memberTieringIdToDelete.value)
      .then(() => {
          showToast({ type: 'success', title: 'Success', message: 'Member tier removed successfully.' });
          load_state();
      }).catch((err) => {
          showToast({ type: 'error', title: 'Error', message: err.message });
          console.log(err);
      }).finally(() => {
          page.isLoading = false;
          isDeleteConfirmationVisible.value = false;
          memberTieringIdToDelete.value = null;
      });
    }

    function cancelDelete() {
      isDeleteConfirmationVisible.value = false;
      memberTieringIdToDelete.value = null;
    }

    function formatCurrency(value) {
      if (!value) return '0.00';
      const formattedValue = parseFloat(value).toFixed(2);
      return formattedValue.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    }

    const route = useRoute();

    function load_state() {
        validation.value = '';
        page.isLoading = true;

        store.dispatch('Auth/getBusinessProfile').then((businessProfile) => {
            const filter = {
                corporate_id: businessProfile.corporate_id,
                tier_id: route.params.id
            }

            store.commit('ShowMemberTier/add_filter', {filter});

            store.dispatch('MemberTier/read_single', route.params.id)
            .then((response) => {
                const transactionValueMin = response.transaction_value_min;

                store.dispatch('ShowMemberTier/read').then((response) => {
                    if (response && Object.keys(response).length > 0) {
                        const membersToSync = response.filter(member => member.isActivated === 1);
                        if (membersToSync.length > 0) {
                            const membersWithInvalidTransactions = membersToSync.filter(member => 
                                member.totalTransactions < transactionValueMin
                            );

                            if (membersWithInvalidTransactions.length > 0) {
                                const memberNames = membersWithInvalidTransactions.map(member => member.memberName).join(', ');
                                validation.value = `Please click sync button to change tier for ${memberNames} at`;
                            }
                        }
                    } else {
                        throw new Error("Response is empty or invalid");
                    }
                })
                .catch((err) => {
                    if (err) {
                        console.error(err);
                    } else {
                        console.error("An unknown error occurred");
                    }
                })
                .finally(() => {
                    page.isLoading = false;
                    page.output = true;
                });
            })
            .catch((err) => console.error(err))
            .finally(() => {
                page.isLoading = false;
                page.output = true;
            });
            if (store.state.Auth.currUser.level === 'root') {
                write.value = true;
            } else {
                const userModules = store.state.Auth.userInfo.modules;

                if (userModules) {
                    const matchingModule = userModules.find(module => module.module_name === page.component);
                    write.value = matchingModule ? matchingModule.write : false;
                } else {
                    console.error('userModules is undefined');
                    write.value = false;
                }
            }
        });
    }

    function navPage(pageNumber) {
      const updatedMeta = { ...meta.value, page: pageNumber };

      store.commit('ShowMemberTier/switch_page', { meta: updatedMeta });
      load_state();
    }

    // function search(searchBy) {
    //   console.log(searchBy);
    //   const { selected, criteria } = searchBy;

    //   if (selected && criteria) {
    //     let search = {};
    //     search[selected] = criteria;

    //     store.commit('ShowMemberTier/set_search', { search });
    //     store.dispatch('ShowMemberTier/read');
    //   }
    // }

    // function clearSearch() {
    //   store.commit('ShowMemberTier/clear_search');
    //   store.dispatch('ShowMemberTier/read')
    //     .then(response => {
    //       isExpanded.before = Array(response.data.length).fill(false);
    //       isExpanded.after = Array(response.data.length).fill(false);
    //     });
    // }

    function formatJson(jsonData) {
      if (!jsonData) return '';
      const test = JSON.parse(jsonData);
      return JSON.stringify(test, null, 2);
    }

    function collapsedJson(jsonData) {
      if (!jsonData) return '';
      const test = JSON.parse(jsonData);
      return JSON.stringify(test, null, 2).slice(0, 2) + '...';
    }

    function toggle(index, type) {
      isExpanded[type][index] = !isExpanded[type][index];
    }

    function formatDate(date) {
      const newDate = new Date(date);

      const options = {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
        timeZone: 'UTC'
      };

      const datePart = newDate.toLocaleDateString('en-US', options).split(', ');

      const formattedDate = `${datePart[0]} on ${datePart[1]} ${datePart[2]}`;

      return formattedDate;
    }

    // function shortenUserId(userId) {
    //   if (!userId) return '';
    //   return userId.slice(0, 5) + '...';
    // }

    return {
      page,
      meta,
      fields,
      load_state,
      navPage,
      error,
      destroy,
      validation,
      // search,
      // clearSearch,
      formatJson,
      formatDate,
      toggle,
      isExpanded,
      collapsedJson,
      // shortenUserId,
      formatCurrency,
      showSyncLink,
      write,
      isDeleteConfirmationVisible,
      confirmDelete,
      cancelDelete
    };
  },
  computed: {
    ...mapState({
      ShowMemberTier: state => state.ShowMemberTier.ShowMemberTier,
      searchBy: state => state.OSearch
    }),
  },
  components: {
    OTable,
    OPagination,
    OBreadCrumb,
    OWarningBox
  }
};
</script>

<style scoped>
.container {
  overflow-x: auto;
}

.table th,
.table td {
  word-wrap: break-word;
  white-space: normal;
}

.form-group {
  width: 250px;
  text-align: left;
  padding-left: 16px;
}

.json-viewer pre {
  white-space: pre-wrap;
  word-wrap: break-word;
  cursor: pointer;
  color: black;
}

.json-viewer span {
  cursor: pointer;
  color: black;
}

.json-viewer button {
  margin-left: 10px;
  cursor: pointer;
  background: none;
  border: none;
  color: black;
}
.modern-alert {
  display: flex;
  align-items: flex-start;
  background-color: #eff6ff;
  border-left: 5px solid #3b82f6;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  animation: fadeIn 0.4s ease-out;
}

.modern-alert .alert-icon {
  flex-shrink: 0;
  margin-right: 14px;
  font-size: 22px;
  color: #3b82f6;
}

.modern-alert .alert-content {
  flex: 1;
}

.modern-alert .alert-title {
  margin: 0 0 6px;
  font-size: 15px;
  font-weight: 700;
  color: #1e3a8a;
}

.modern-alert .alert-message {
  margin: 0;
  font-size: 14px;
  color: #1e40af;
  line-height: 1.5;
}

.modern-alert .alert-link {
  color: #1d4ed8;
  font-weight: 600;
  text-decoration: underline;
  margin-left: 4px;
  transition: color 0.2s;
}

.modern-alert .alert-link:hover {
  color: #1e3a8a;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Styling from Member.vue for consistency */
.filter-action-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 20px;
    flex-wrap: wrap;
    background-color: #fff;
    padding: 16px;
    border-radius: 12px;
    border: 1px solid #e2e8f0;
}

.filter-controls {
    display: flex;
    align-items: flex-end;
    gap: 12px;
    flex-wrap: wrap;
    flex: 1;
}

.search-box-custom {
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: max-content;
}

.search-type-tabs {
    display: flex;
    gap: 4px;
    background: #f1f5f9;
    padding: 4px;
    border-radius: 8px;
    width: fit-content;
    flex-wrap: wrap;
}

.tab-btn {
    border: none;
    background: transparent;
    padding: 6px 14px;
    font-size: 12px;
    font-weight: 500;
    color: #64748b;
    border-radius: 6px;
    transition: all 0.2s;
    cursor: pointer;
}

.tab-btn.active {
    background: white;
    color: var(--primary-color, #0d6efd);
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
    font-weight: 600;
}

.search-input-wrapper {
    display: flex;
    align-items: center;
    gap: 0;
    position: relative;
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 0;
    transition: all 0.2s;
    overflow: hidden;
}

.search-input-wrapper:focus-within {
    border-color: var(--primary-color, #0d6efd);
    box-shadow: 0 0 0 2px rgba(13, 110, 253, 0.1);
}

.search-input-wrapper input {
    border: none;
    padding: 10px 16px;
    font-size: 14px;
    width: 100%;
    outline: none;
    background: transparent;
    color: #334155;
    box-shadow: none;
}

.btn-search {
    background: #f8fafc;
    border: none;
    width: 36px;
    height: 36px;
    border-radius: 6px;
    color: #64748b;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;
    flex-shrink: 0;
}

.btn-search:hover {
    background: #e2e8f0;
    color: var(--primary-color, #0d6efd);
}

.search-input-wrapper .btn-search {
    position: relative;
    width: 44px;
    height: 42px;
    border-radius: 0;
    background: transparent;
}

.search-input-wrapper .btn-search:hover {
    background: #f1f5f9;
}

.search-input-wrapper .btn-search::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 1.1px;
    height: 60%;
    background: linear-gradient(to bottom, rgba(203, 213, 225, 0), rgba(203, 213, 225, 1), rgba(203, 213, 225, 0));
    pointer-events: none;
}

.action-section {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.action-buttons-group {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
}

.btn-refresh {
    background-color: #fff;
    border: 1px solid #e2e8f0;
    color: #64748b;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
    border-radius: 8px;
}

.btn-refresh:hover {
    background-color: #f1f5f9;
    color: #1e293b;
    border-color: #cbd5e1;
}

.btn-action-secondary {
    background-color: #f8fafc;
    border: 1px solid #e2e8f0;
    color: #475569;
    font-weight: 600;
    font-size: 13px;
    padding: 0 16px;
    height: 40px;
    display: flex;
    align-items: center;
    border-radius: 8px;
    transition: all 0.2s;
    text-decoration: none;
    cursor: pointer;
}

.btn-action-secondary:hover {
    background-color: #e2e8f0;
    color: #1e293b;
}

.btn-add {
    background-color: var(--primary-color, #0d6efd);
    color: white;
    font-weight: 600;
    font-size: 13px;
    padding: 0 20px;
    height: 40px;
    display: flex;
    align-items: center;
    border: none;
    border-radius: 8px;
    transition: all 0.2s;
    text-decoration: none;
}

.btn-add:hover {
    filter: brightness(0.9);
    color: white;
}

.member-list-table {
    white-space: nowrap;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    overflow: hidden;
}

.member-list-table th,
.member-list-table td {
    padding: 12px 16px !important;
    vertical-align: middle;
}

.member-row-clickable {
    transition: background-color 0.2s;
}

.member-row-clickable:hover {
    background-color: #f8fafc !important;
}

.btn-action {
    padding: 6px 14px;
    font-size: 12px;
    font-weight: 600;
    border-radius: 8px;
    transition: all 0.2s ease;
    display: inline-flex;
    align-items: center;
    text-decoration: none;
    border: none;
    cursor: pointer;
}

.btn-info-action {
    background-color: #eff6ff;
    color: #1e40af;
}

.btn-info-action:hover {
    background-color: #dbeafe;
    color: #1e3a8a;
}

.btn-edit {
    background-color: #f3f4f6;
    color: #374151;
}

.btn-edit:hover {
    background-color: #e5e7eb;
    color: #111827;
}

.btn-delete {
    background-color: #fff1f2;
    color: #9f1239;
}

.btn-delete:hover {
    background-color: #ffe4e6;
    color: #be123c;
}

.header-title {
    font-size: 1.5rem;
    font-weight: 600;
    color: #111827;
    margin: 0 0 0.25rem 0;
    display: flex;
    align-items: center;
}

.header-subtitle {
    font-size: 0.875rem;
    color: #6b7280;
    margin: 0;
    text-align: left;
}
</style>